import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const { orderId: orderIdParam } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);
  const verificationInitiatedRef = useRef(false);

  // Extract numeric order ID from path parameter (handle comma-separated values like "1643,EZK1765292286690476")
  const orderId = orderIdParam && orderIdParam.includes(',') 
    ? orderIdParam.split(',')[0].trim() 
    : orderIdParam;

  // Helper function to categorize payment statuses (matches backend logic)
  // Handles all 6 HDFC Gateway statuses: PENDING_VBV, AUTHORIZING, CHARGED, AUTHORIZATION_FAILED, AUTHENTICATION_FAILED, JUSPAY_DECLINED
  const categorizePaymentStatus = (status) => {
    if (!status) return { type: 'unknown', isSuccess: false, isPending: false, isFailure: false };
    
    const normalizedStatus = status.toUpperCase();
    
    // Success statuses
    const successStatuses = ['CHARGED', 'SUCCESS', 'CAPTURED', 'AUTHORIZED', 'COMPLETED', 'SETTLED'];
    if (successStatuses.includes(normalizedStatus)) {
      return { type: 'success', isSuccess: true, isPending: false, isFailure: false };
    }
    
    // Pending/In-progress statuses
    const pendingStatuses = ['PENDING', 'PENDING_VBV', 'AUTHORIZING', 'PROCESSING', 'INITIATED', 'AWAITING_PAYMENT'];
    if (pendingStatuses.includes(normalizedStatus)) {
      return { type: 'pending', isSuccess: false, isPending: true, isFailure: false };
    }
    
    // Failure statuses - includes all HDFC Gateway failure statuses
    const failureStatuses = [
      'FAILED', 
      'CANCELLED', 
      'DECLINED', 
      'REJECTED', 
      'EXPIRED', 
      'ERROR',
      'AUTHORIZATION_FAILED',      // HDFC Gateway specific failure
      'AUTHENTICATION_FAILED',     // HDFC Gateway specific failure
      'JUSPAY_DECLINED'            // HDFC Gateway specific failure
    ];
    if (failureStatuses.includes(normalizedStatus)) {
      return { type: 'failure', isSuccess: false, isPending: false, isFailure: true };
    }
    
    // Default: unknown status
    return { type: 'unknown', isSuccess: false, isPending: false, isFailure: false };
  };

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data.order);
      setError('');
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || 'Failed to load order details. Please try again.';
      setError(errorMessage);
      console.error(err);
      
      // Redirect to schools page on error
      setTimeout(() => {
        navigate('/schools');
      }, 3000);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const verifyPayment = useCallback(async (orderId, paymentId, hdfcOrderId, paymentStatus) => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const response = await api.post(`/orders/${orderId}/verify-payment`, {
        hdfc_order_id: hdfcOrderId,
        hdfc_payment_id: paymentId,
        payment_status: paymentStatus
      });

      if (response.data.order) {
        // Use the order from verification response instead of making another API call
        setOrder(response.data.order);
        
        // If payment is pending, show appropriate message
        if (response.data.is_pending) {
          setError('Your payment is being processed. Please wait for confirmation. You will receive an email once the payment is confirmed.');
        } else {
          // Payment is successful - check if we need to refresh
          const order = response.data.order;
          if (order.payment_status === 'paid' || order.status === 'confirmed') {
            // Payment completed successfully - refresh the page to clear cart and update UI
            // The backend already clears the cart, but we need to refresh to update the UI
            setTimeout(() => {
              window.location.reload();
            }, 1500); // Give time for user to see success message
          }
        }
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setError('Payment verification failed. Please contact support if payment was deducted.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      const errorMessage = err.userFriendlyMessage || err.response?.data?.error || 'Payment verification failed. Please contact support if payment was deducted.';
      setError(errorMessage);
      
      // Redirect to schools page after showing error
      setTimeout(() => {
        navigate('/schools');
      }, 5000);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Handle HDFC Gateway callback parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('payment_id') || urlParams.get('hdfc_payment_id') || urlParams.get('transaction_id');
    const hdfcOrderId = urlParams.get('order_id') || urlParams.get('hdfc_order_id');
    const status = urlParams.get('status') || urlParams.get('payment_status');
    const isPending = urlParams.get('pending') === 'true';
    
    // Extract numeric order ID from hdfcOrderId if it's comma-separated
    const numericHdfcOrderId = hdfcOrderId && hdfcOrderId.includes(',') 
      ? hdfcOrderId.split(',')[0].trim() 
      : hdfcOrderId;
    
    // Categorize the payment status
    const statusCategory = status ? categorizePaymentStatus(status) : { isSuccess: false, isPending: false, isFailure: false };
    
    // Create a unique key for this verification attempt to prevent duplicates
    const verificationKey = `${orderId}-${paymentId || numericHdfcOrderId || ''}-${status || ''}`;
    
    // If payment parameters are present and status indicates success or pending, verify payment first
    // This will update the order state, so we don't need to fetch separately
    if ((statusCategory.isSuccess || statusCategory.isPending || isPending) && numericHdfcOrderId && numericHdfcOrderId === orderId) {
      // Check if we've already initiated verification for this specific combination
      if (verificationInitiatedRef.current !== verificationKey) {
        verificationInitiatedRef.current = verificationKey;
        // If paymentId is available, use it; otherwise pass hdfcOrderId as paymentId for verification
        verifyPayment(orderId, paymentId || numericHdfcOrderId, numericHdfcOrderId || hdfcOrderId, status);
      }
    } else if (paymentId && numericHdfcOrderId && numericHdfcOrderId === orderId) {
      // Check if we've already initiated verification for this specific combination
      if (verificationInitiatedRef.current !== verificationKey) {
        verificationInitiatedRef.current = verificationKey;
        // Fallback: verify if paymentId is present
        verifyPayment(orderId, paymentId, numericHdfcOrderId || hdfcOrderId, status);
      }
    } else {
      // No payment verification needed, just fetch the order (only if not already verifying)
      if (!verificationInitiatedRef.current) {
        fetchOrder();
      }
    }
  }, [orderId, fetchOrder, verifyPayment]);

  const handlePrint = () => {
    setIsPrinting(true);
    window.print();
    setTimeout(() => setIsPrinting(false), 1000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatusTitle = (status) => {
    const statusLower = status.toLowerCase();
    const statusMap = {
      'pending': 'Order Pending',
      'confirmed': 'Order Confirmed',
      'processing': 'Order Processing',
      'shipped': 'Order Shipped',
      'delivered': 'Order Delivered',
      'cancelled': 'Order Cancelled'
    };
    return statusMap[statusLower] || `Order ${status}`;
  };

  const getOrderStatusMessage = (status, paymentStatus) => {
    const statusLower = status.toLowerCase();
    const paymentLower = paymentStatus?.toUpperCase() || '';
    
    if (statusLower === 'cancelled') {
      return 'Your order has been cancelled.';
    }
    
    // Handle specific payment failure statuses
    if (paymentLower === 'AUTHORIZATION_FAILED') {
      return 'Payment authorization failed. Please try again or use a different payment method.';
    }
    if (paymentLower === 'AUTHENTICATION_FAILED') {
      return 'Payment authentication failed. Please verify your payment details and try again.';
    }
    if (paymentLower === 'JUSPAY_DECLINED') {
      return 'Payment was declined by the payment gateway. Please try again or use a different payment method.';
    }
    
    // Handle pending payment statuses
    if (paymentLower === 'PENDING_VBV' || paymentLower === 'AUTHORIZING' || paymentLower === 'PROCESSING') {
      return 'Your payment is being processed. Please wait for confirmation.';
    }
    
    if (statusLower === 'pending' && (paymentLower === 'PENDING' || paymentLower === 'PENDING_VBV' || paymentLower === 'AUTHORIZING')) {
      return 'Your order has been placed and payment is being processed.';
    }
    
    if (statusLower === 'pending') {
      return 'Your order has been placed and is being processed.';
    }
    
    if (statusLower === 'confirmed') {
      return 'Thank you for your purchase. Your order has been confirmed.';
    }
    
    if (statusLower === 'processing') {
      return 'Your order is being processed and will be shipped soon.';
    }
    
    if (statusLower === 'shipped') {
      return 'Your order has been shipped and is on its way.';
    }
    
    if (statusLower === 'delivered') {
      return 'Your order has been delivered successfully.';
    }
    
    return 'Thank you for your purchase.';
  };

  const getOrderStages = (status) => {
    const stages = [
      { key: 'pending', label: 'Pending', icon: '⏳' },
      { key: 'confirmed', label: 'Confirmed', icon: '✅' },
      { key: 'processing', label: 'Processing', icon: '🔄' },
      { key: 'shipped', label: 'Shipped', icon: '📦' },
      { key: 'delivered', label: 'Delivered', icon: '🎉' }
    ];

    const statusLower = status.toLowerCase();
    const currentIndex = stages.findIndex(s => s.key === statusLower);
    
    if (statusLower === 'cancelled') {
      return { stages: [], isCancelled: true };
    }

    return {
      stages: stages.map((stage, index) => ({
        ...stage,
        completed: index < currentIndex,
        active: index === currentIndex,
        pending: index > currentIndex
      })),
      isCancelled: false
    };
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="order-confirmation-page">
          <div className="container">
            <div className="confirmation-loading">
              <div className="loading-spinner"></div>
              <p>Loading order details...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="App">
        <Header />
        <div className="order-confirmation-page">
          <div className="container">
            <div className="confirmation-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <h2>{error || 'Order not found'}</h2>
              <Link to="/orders" className="btn-back-to-orders">Back to Orders</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const items = Array.isArray(order.items) ? order.items : [];
  const totalAmount = parseFloat(order.total_amount) + parseFloat(order.delivery_charge);

  return (
    <div className="App">
      <Header />
      <div className="order-confirmation-page">
        <div className="container">
          {/* Action Buttons - Hidden in Print */}
          <div className="confirmation-actions-bar no-print">
            <div className="actions-left">
              <Link to="/orders" className="action-link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                <span>Back to Orders</span>
              </Link>
            </div>
            <div className="actions-right">
              <button 
                onClick={handlePrint} 
                className="action-btn action-btn-print"
                disabled={isPrinting}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
                <span>{isPrinting ? 'Printing...' : 'Print'}</span>
              </button>
            </div>
          </div>

          {/* Print Content */}
          <div ref={printRef} className="confirmation-content">
            {/* Order Status Header */}
            <div className="confirmation-header">
              <div className="success-icon-wrapper">
                {order.status.toLowerCase() === 'cancelled' ? (
                  <div className="success-icon cancelled-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                  </div>
                ) : order.status.toLowerCase() === 'delivered' ? (
                  <div className="success-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                ) : order.status.toLowerCase() === 'confirmed' ? (
                  <div className="success-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                ) : (
                  <div className="success-icon pending-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </div>
                )}
                {order.status.toLowerCase() !== 'cancelled' && order.status.toLowerCase() !== 'pending' && (
                  <div className="success-rings"></div>
                )}
              </div>
              <h1 className="confirmation-title">{getOrderStatusTitle(order.status)}!</h1>
              <p className="confirmation-message">
                {getOrderStatusMessage(order.status, order.payment_status)}
              </p>
            </div>

            {/* Prominent Payment Response Information - Real-time Display */}
            {order.status.toLowerCase() === 'confirmed' && order.payment_status?.toLowerCase() === 'paid' && (
              <div className="payment-response-summary">
                <div className="payment-response-card">
                  <h2 className="payment-response-title">Payment Successful</h2>
                  <div className="payment-response-details">
                    <div className="payment-response-item">
                      <span className="payment-response-label">Order Number</span>
                      <span className="payment-response-value order-number-highlight">#{order.order_number}</span>
                    </div>
                    <div className="payment-response-item">
                      <span className="payment-response-label">Amount Paid</span>
                      <span className="payment-response-value amount-highlight">₹{totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="payment-response-message">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Your payment has been processed successfully. Order confirmation details are shown below.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Status Workflow */}
            {(() => {
              const workflow = getOrderStages(order.status);
              if (workflow.isCancelled) {
                return (
                  <div className="order-workflow-container">
                    <div className="workflow-cancelled">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      <span>Order Cancelled</span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="order-workflow-container">
                  <div className="order-workflow">
                    {workflow.stages.map((stage, index) => (
                      <React.Fragment key={stage.key}>
                        <div className={`workflow-step ${stage.completed ? 'completed' : ''} ${stage.active ? 'active' : ''} ${stage.pending ? 'pending' : ''}`}>
                          <div className="workflow-step-icon">
                            {stage.completed ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            ) : (
                              <span className="workflow-icon-text">{stage.icon}</span>
                            )}
                          </div>
                          <div className="workflow-step-label">{stage.label}</div>
                        </div>
                        {index < workflow.stages.length - 1 && (
                          <div className={`workflow-connector ${stage.completed ? 'completed' : ''} ${stage.active ? 'active' : ''}`}></div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Order Details Grid */}
            <div className="order-details-grid">
              {/* Order Information Card */}
              <div className="order-info-card-modern">
                <div className="card-header">
                  <div className="card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    </svg>
                  </div>
                  <h2 className="card-title">Order Information</h2>
                </div>
                <div className="card-content">
                  <div className="info-row-modern">
                    <span className="info-label">Order Number</span>
                    <span className="info-value order-number-value">#{order.order_number}</span>
                  </div>
                  <div className="info-row-modern">
                    <span className="info-label">Order Date</span>
                    <span className="info-value">{formatDate(order.created_at)}</span>
                  </div>
                  <div className="info-row-modern">
                    <span className="info-label">Payment Status</span>
                    <span className={`status-badge status-${order.payment_status.toLowerCase()}`}>
                      {order.payment_status}
                    </span>
                  </div>
                  <div className="info-row-modern">
                    <span className="info-label">Order Status</span>
                    <span className={`status-badge status-${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>
                  {order.estimated_delivery_date && (
                    <div className="info-row-modern">
                      <span className="info-label">Estimated Delivery</span>
                      <span className="info-value">{new Date(order.estimated_delivery_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items Card */}
              <div className="order-items-card-modern">
                <div className="card-header">
                  <div className="card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                    </svg>
                  </div>
                  <h2 className="card-title">Order Items ({items.length})</h2>
                </div>
                <div className="card-content">
                  <div className="order-items-list">
                    {items.map((item, idx) => (
                      <div key={idx} className="order-item-modern">
                        <div className="item-info">
                          <div className="item-badge">{formatStandard(item.standard)}</div>
                          <h3 className="item-name">{item.kit_name}</h3>
                          <div className="item-meta">
                            <span className="item-quantity">Quantity: {item.quantity}</span>
                          </div>
                        </div>
                        <div className="item-price-section">
                          <span className="item-price">₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary Card */}
              <div className="order-summary-card-modern">
                <div className="card-header">
                  <div className="card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  </div>
                  <h2 className="card-title">Order Summary</h2>
                </div>
                <div className="card-content">
                  <div className="summary-breakdown">
                    <div className="summary-row-modern">
                      <span className="summary-label">Subtotal</span>
                      <span className="summary-value">₹{parseFloat(order.total_amount).toFixed(2)}</span>
                    </div>
                    <div className="summary-row-modern">
                      <span className="summary-label">Delivery Charge</span>
                      <span className="summary-value">₹{parseFloat(order.delivery_charge).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="summary-divider"></div>
                  <div className="summary-total-section">
                    <span className="total-label-large">Total Amount</span>
                    <span className="total-amount-large">₹{totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Information Card */}
              <div className="delivery-info-card-modern">
                <div className="card-header">
                  <div className="card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <h2 className="card-title">Delivery Information</h2>
                </div>
                <div className="card-content">
                  <div className="delivery-details">
                    <div className="delivery-type">
                      <span className="delivery-label">Delivery Type:</span>
                      <span className="delivery-value">
                        {order.delivery_type === 'home_delivery' ? 'Home Delivery' : 'School Pickup'}
                      </span>
                    </div>
                    {order.delivery_address && (
                      <div className="delivery-address">
                        <span className="delivery-label">Delivery Address:</span>
                        <span className="delivery-value">{order.delivery_address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="confirmation-footer-note">
              <p>We'll send you an email confirmation shortly. If you have any questions, please contact our support team.</p>
            </div>
          </div>

          {/* Action Buttons - Hidden in Print */}
          <div className="confirmation-actions-bottom no-print">
            <Link to="/schools" className="btn-continue-shopping">
              <span>Continue Shopping</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14"></path>
                <path d="M12 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderConfirmation;
